import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useStore, WorkspaceData, Draft, StorageKey, ApiError, useWorkspaceMutation } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { useBreadcrumb } from '@dms/hook'
import { LayoutHeader, WorkspaceAddForm } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const WorkspaceAdd = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const store = useStore()
  const { message, storage } = store
  const [{ companyId }] = useNumberParams()
  const [data, setData] = useState<WorkspaceData>({ id: 0 })
  const { t } = useTranslation()
  const mutation = useWorkspaceMutation({ companyId })

  const breadcrumb = useBreadcrumb([
    {
      title: t('Workspaces'),
      href: `/companies/${companyId}/workspaces`,
    },
    {
      title: t('Add workspace'),
    },
  ])

  useEffect(() => {
    const loadDefaults = async () => {
      const newData = { ...data }
      storage.get(`${companyId}:${StorageKey.Floor}`, undefined, (data) => {
        const floorId = parseInt(data || '', 10)
        if (!isNaN(floorId)) newData.floor_id = floorId
        setData(newData)
      })
    }
    loadDefaults()
  }, [])

  const add = async (draft: Draft<WorkspaceData>) => {
    try {
      setErrors({})
      await mutation.create(draft)
      message.add({
        message: t('Workspace was created successfully'),
        status: 'success',
      })
      draft.data.floor_id && storage.set(`${companyId}:${StorageKey.Floor}`, `${draft.data.floor_id}`)
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`WORKSPACE: Workspace failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Workspace could not be created')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }

    router.push(`/companies/${companyId}/workspaces`)
  }

  return (
    <View style={styles.container}>
      <LayoutHeader breadcrumb={breadcrumb} title={t('Add workspace')} />
      <View style={styles.content}>
        <Suspense fallback={<WorkspaceAddForm.Skeleton />}>
          <WorkspaceAddForm companyId={companyId} data={data} onSave={add} errors={errors} />
        </Suspense>
      </View>
    </View>
  )
}

export default WorkspaceAdd
